import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Package, Palette, Shield, MessageSquare, Activity, DollarSign, Gift, Tag, Youtube, RefreshCw, Calendar, Database, Loader2 } from 'lucide-react';
import { getAuthHeaders } from '../context/AuthContext';
import AdminLayout from '../components/AdminLayout';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config/api';

const TYPE_COLORS = {
  bug: { fill: '#ef4444', label: 'Bug' },
  feature: { fill: '#f59e0b', label: 'Feature' },
  billing: { fill: '#10b981', label: 'Billing' },
  usability: { fill: '#6366f1', label: 'Usability' },
  general: { fill: '#3b82f6', label: 'General' },
  praise: { fill: '#ec4899', label: 'Praise' },
};

function DonutChart({ data, size = 120 }) {
  const entries = Object.entries(data).filter(([, v]) => v > 0);
  const total = entries.reduce((s, [, v]) => s + v, 0);
  if (total === 0) {
    return (
      <div className="flex items-center justify-center" style={{ width: size, height: size }}>
        <div className="w-full h-full rounded-full border-8 border-beige flex items-center justify-center">
          <span className="text-xs text-gray-light">No data</span>
        </div>
      </div>
    );
  }

  // Build conic-gradient stops
  let angle = 0;
  const stops = [];
  entries.forEach(([type, count]) => {
    const color = TYPE_COLORS[type]?.fill || '#94a3b8';
    const sweep = (count / total) * 360;
    stops.push(`${color} ${angle}deg ${angle + sweep}deg`);
    angle += sweep;
  });

  const holeSize = size * 0.58;

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <div
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          background: `conic-gradient(from -90deg, ${stops.join(', ')})`,
        }}
      />
      {/* Center hole */}
      <div
        className="absolute bg-white"
        style={{
          width: holeSize,
          height: holeSize,
          borderRadius: '50%',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
        }}
      />
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-lg font-semibold text-charcoal">{total}</span>
      </div>
    </div>
  );
}

export default function AdminOverview() {
  const [stats, setStats] = useState(null);
  const [health, setHealth] = useState(null);
  const [r2Status, setR2Status] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = getAuthHeaders();
        const [statsRes, healthRes, r2Res] = await Promise.all([
          fetch(`${BACKEND_URL}/api/admin/stats`, { credentials: 'include', headers }),
          fetch(`${BACKEND_URL}/api/health`),
          fetch(`${BACKEND_URL}/api/admin/r2-status`, { credentials: 'include', headers })
        ]);
        if (statsRes.ok) setStats(await statsRes.clone().json());
        if (healthRes.ok) setHealth(await healthRes.clone().json());
        if (r2Res.ok) setR2Status(await r2Res.clone().json());
      } catch (err) {
        console.error('Failed to fetch stats:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const refreshHealth = useCallback(async () => {
    setRefreshing(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/health`);
      if (res.ok) setHealth(await res.clone().json());
    } catch (err) {
      console.error('Health refresh failed:', err);
    } finally {
      setRefreshing(false);
    }
  }, []);

  const seedTestData = async () => {
    if (seeding) return;
    setSeeding(true);
    try {
      const headers = getAuthHeaders();
      const res = await fetch(`${BACKEND_URL}/api/admin/seed-test-data`, {
        method: 'POST',
        credentials: 'include',
        headers,
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Seeded ${data.items_created} test items for ${data.test_user_email}`);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to seed test data');
      }
    } catch (err) {
      console.error('Seed failed:', err);
      toast.error('Failed to seed test data');
    } finally {
      setSeeding(false);
    }
  };

  const tiles = stats ? [
    { label: 'Billing', value: null, icon: DollarSign, color: 'text-green-600 bg-green-50', link: '/admin/billing',
      custom: (
        <div className="space-y-1 mt-1">
          <div className="flex items-center justify-between">
            <span className="text-[11px] text-gray-light">MRR</span>
            <span className="text-sm font-semibold text-green-600">${stats.billing_mrr?.toFixed(2) || '0.00'}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-[11px] text-gray-light">Paid</span>
            <span className="text-[11px] font-medium text-charcoal">{stats.billing_paid || 0}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-[11px] text-gray-light">Free</span>
            <span className="text-[11px] font-medium text-charcoal">{stats.billing_free || 0}</span>
          </div>
          {stats.billing_unlimited > 0 && (
            <div className="flex items-center justify-between">
              <span className="text-[11px] text-gray-light">Unlimited</span>
              <span className="text-[11px] font-medium text-charcoal">{stats.billing_unlimited}</span>
            </div>
          )}
        </div>
      )
    },
    { label: 'Promo Codes', value: null, icon: Gift, color: 'text-pink-600 bg-pink-50', link: '/admin/promo-codes' },
    { label: 'Total Users', value: stats.total_users, icon: Users, color: 'text-blue-600 bg-blue-50', link: '/admin/users' },
    { label: 'Categories', value: null, icon: Tag, color: 'text-amber-600 bg-amber-50', link: '/admin/categories' },
    { label: 'Occasions', value: null, icon: Calendar, color: 'text-rose-600 bg-rose-50', link: '/admin/occasions' },
    { label: 'Knowledge Base', value: null, icon: Youtube, color: 'text-red-600 bg-red-50', link: '/admin/youtube' },
    { label: 'Stash Items', value: stats.total_items, icon: Package, color: 'text-sage bg-sage-light', link: null, sub: `${stats.avg_items_per_user ?? '—'} avg / user` },
    { label: 'Designs Created', value: stats.total_designs, icon: Palette, color: 'text-purple-600 bg-purple-50', link: null, sub: `${stats.avg_designs_per_user ?? '—'} avg / user` },
  ] : [];

  return (
    <AdminLayout>
      <div data-testid="admin-overview">
        <h1 className="text-2xl font-medium text-charcoal mb-6">Overview</h1>

        {loading ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[1,2,3,4,5,6].map(i => (
              <div key={i} className="bg-white rounded-xl p-6 border border-beige animate-pulse h-28" />
            ))}
          </div>
        ) : (
          <div className="space-y-6">
            {/* Stat tiles — clickable */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {tiles.map(tile => {
                const Icon = tile.icon;
                const isClickable = !!tile.link;
                return (
                  <div
                    key={tile.label}
                    onClick={() => isClickable && navigate(tile.link)}
                    className={`bg-white rounded-xl p-5 border border-beige transition-all duration-200 ${
                      isClickable ? 'cursor-pointer hover:border-sage hover:shadow-md hover:-translate-y-0.5' : ''
                    }`}
                    data-testid={`stat-${tile.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${tile.color}`}>
                      <Icon size={20} />
                    </div>
                    {tile.value !== null && tile.value !== undefined ? (
                      <p className="text-2xl font-semibold text-charcoal">{tile.value.toLocaleString()}</p>
                    ) : null}
                    <p className="text-sm text-gray-soft mt-0.5">{tile.label}</p>
                    {tile.sub && (
                      <p className="text-[11px] text-gray-light mt-0.5">{tile.sub}</p>
                    )}
                    {tile.custom && tile.custom}
                    {isClickable && !tile.custom && (
                      <p className="text-[10px] text-sage mt-1">Click to manage</p>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Bottom row: Feedback + Services */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Feedback tile */}
              <div
                onClick={() => navigate('/admin/feedback')}
                className="bg-white rounded-xl p-6 border border-beige cursor-pointer hover:border-sage hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
                data-testid="stat-feedback"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-orange-50 text-orange-600">
                      <MessageSquare size={20} />
                    </div>
                    <div>
                      <h3 className="text-base font-medium text-charcoal">User Feedback</h3>
                      {stats?.feedback_new > 0 && (
                        <span className="text-xs font-medium text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full">
                          {stats.feedback_new} new
                        </span>
                      )}
                    </div>
                  </div>
                  <p className="text-[10px] text-sage">Click to manage</p>
                </div>

                <div className="flex items-center gap-6">
                  <DonutChart data={stats?.feedback_by_type || {}} size={100} />
                  <div className="space-y-1.5 flex-1">
                    {Object.entries(TYPE_COLORS).map(([type, cfg]) => {
                      const count = stats?.feedback_by_type?.[type] || 0;
                      return (
                        <div key={type} className="flex items-center gap-2">
                          <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: cfg.fill }} />
                          <span className="text-xs text-gray-soft flex-1">{cfg.label}</span>
                          <span className="text-xs font-medium text-charcoal">{count}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Needs Attention + Status bar */}
                <div className="mt-4 pt-3 border-t border-beige">
                  {stats?.feedback_needs_attention > 0 && (
                    <div className="flex items-center gap-2 mb-2.5" data-testid="needs-attention-badge">
                      <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                      <span className="text-xs font-semibold text-amber-700">
                        {stats.feedback_needs_attention} need{stats.feedback_needs_attention === 1 ? 's' : ''} attention
                      </span>
                      <span className="text-[10px] text-gray-light">(bug · billing · usability)</span>
                    </div>
                  )}
                  <div className="flex items-center gap-3 text-[10px] text-gray-soft" data-testid="feedback-status-bar">
                    {[
                      { key: 'new', label: 'New', color: 'bg-orange-400' },
                      { key: 'in_progress', label: 'In Progress', color: 'bg-blue-400' },
                      { key: 'reviewed', label: 'Reviewed', color: 'bg-purple-400' },
                      { key: 'resolved', label: 'Resolved', color: 'bg-green-400' },
                      { key: 'wont_fix', label: "Won't Fix", color: 'bg-gray-400' },
                    ].map(s => {
                      const c = stats?.feedback_by_status?.[s.key] || 0;
                      return c > 0 ? (
                        <span key={s.key} className="flex items-center gap-1">
                          <span className={`w-1.5 h-1.5 rounded-full ${s.color}`} />
                          {c} {s.label}
                        </span>
                      ) : null;
                    })}
                  </div>
                </div>
              </div>

              {/* Services tile with detail + refresh */}
              <div
                onClick={() => navigate('/admin/services')}
                className="bg-white rounded-xl p-6 border border-beige cursor-pointer hover:border-sage hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
                data-testid="stat-services"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      health?.status === 'healthy' ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'
                    }`}>
                      <Activity size={20} />
                    </div>
                    <div>
                      <h3 className="text-base font-medium text-charcoal">Services</h3>
                      <span className={`text-xs font-medium ${
                        health?.status === 'healthy' ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {health?.status === 'healthy' ? 'All systems operational' : 'Issues detected'}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        refreshHealth();
                      }}
                      className="p-2 rounded-lg hover:bg-beige transition-colors text-gray-soft hover:text-charcoal"
                      title="Refresh health status"
                      data-testid="refresh-health-btn"
                    >
                      <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
                    </button>
                    <p className="text-[10px] text-sage">Click to manage</p>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between py-2 border-b border-beige">
                    <span className="text-sm text-gray-soft">API Status</span>
                    <span className={`text-sm font-medium flex items-center gap-1.5 ${
                      health?.status === 'healthy' ? 'text-green-600' : 'text-red-600'
                    }`}>
                      <span className={`w-2 h-2 rounded-full ${health?.status === 'healthy' ? 'bg-green-500' : 'bg-red-500'}`} />
                      {health?.status === 'healthy' ? 'Operational' : 'Issues Detected'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-beige">
                    <span className="text-sm text-gray-soft">Database</span>
                    <span className="text-sm font-medium text-green-600 flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-green-500" />
                      Connected
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-beige">
                    <span className="text-sm text-gray-soft">AI Services</span>
                    <span className="text-sm font-medium text-green-600 flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-green-500" />
                      Available
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-beige">
                    <span className="text-sm text-gray-soft">Storage (R2)</span>
                    <span className={`text-sm font-medium flex items-center gap-1.5 ${
                      r2Status?.r2_configured ? 'text-green-600' : 'text-red-600'
                    }`}>
                      <span className={`w-2 h-2 rounded-full ${r2Status?.r2_configured ? 'bg-green-500' : 'bg-red-500'}`} />
                      {r2Status?.r2_configured
                        ? `${r2Status.detected_env} · ${r2Status.total_images} images`
                        : 'Not configured'}
                    </span>
                  </div>
                  {health?.timestamp && (
                    <div className="flex items-center justify-between py-2">
                      <span className="text-sm text-gray-soft">Last Check</span>
                      <span className="text-xs text-gray-light font-mono">
                        {new Date(health.timestamp).toLocaleString()}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Developer Tools */}
            <div className="bg-white rounded-xl p-6 border border-beige" data-testid="dev-tools-section">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-gray-100 text-gray-600">
                  <Database size={20} />
                </div>
                <div>
                  <h3 className="text-base font-medium text-charcoal">Developer Tools</h3>
                  <span className="text-xs text-gray-light">Testing & development utilities</span>
                </div>
              </div>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={seedTestData}
                  disabled={seeding}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-sage text-white rounded-lg hover:bg-sage-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="seed-test-data-btn"
                >
                  {seeding ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <Database size={16} />
                  )}
                  {seeding ? 'Seeding...' : 'Seed 500 Test Items'}
                </button>
                <span className="self-center text-xs text-gray-light">
                  Adds 500 realistic items to your stash for testing
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
